import { Typography } from '@mui/material'

import {
  ConsoleItemsConnectionFragment,
  Maybe,
  MissionType,
} from '@/clients/utoniq-core/schema'
import { Head } from '@/components/layouts'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { ArtistThemeProvider } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { Place, Prediction } from '@/lib/googleMap'

import {
  FileUploadInfo,
  MissionCreateForm,
  MissionCreateFormProps,
} from './MissionCreateForm'

export * from './fragments'

export type State = {
  artistId: string
  missionType: MissionType
  progress: number
  items: Maybe<ConsoleItemsConnectionFragment>
  loading: boolean
  hasNextPage: boolean
  predictions: Prediction[]
  place: Maybe<Place>
  gpsClientInfo: MissionCreateFormProps['gpsClientInfo']
}

type HeadingTextCellProps = {
  missionType: MissionType
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onSubmit: MissionCreateFormProps['onSubmit']
  onDrop: FileUploadInfo['onDrop']
  onFetchMore: () => Promise<void>
  onSearch: (value: { keyword: string }) => Promise<void>
  onPlaceSearch: (value: { keyword: string }) => Promise<void>
  onSelectPlace: (value: {
    placeId: string
    map: google.maps.Map
  }) => Promise<void>
  onRequestGPSPermission: () => void
  removePlace: () => void
}

export const ConsoleMissionCreateTemplate: Template<State, Action> = ({
  state,
  onSubmit,
  onDrop,
  onFetchMore,
  onSearch,
  onPlaceSearch,
  onSelectPlace,
  onRequestGPSPermission,
  removePlace,
}) => {
  const { routerNames, routerPages } = useRouter()
  const { t } = useTranslation('consoleMissionCreate')
  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.ConsoleMissionCreate} />

      <ConsoleBaseLayout
        header={{
          back: true,
          defaultBackPage: routerPages.ConsoleMissions,
          title: t('title'),
        }}
      >
        <HeadingTextCell missionType={state.missionType} />
        <MissionCreateForm
          artistId={state.artistId}
          missionType={state.missionType}
          onSubmit={onSubmit}
          predictions={state.predictions}
          place={state.place}
          onSelectPlace={onSelectPlace}
          onPlaceSearch={onPlaceSearch}
          fileUploadInfo={{
            onDrop,
            progress: state.progress,
          }}
          loading={state.loading}
          hasNextPage={state.hasNextPage}
          items={state.items}
          onFetchMore={onFetchMore}
          onSearch={onSearch}
          gpsClientInfo={state.gpsClientInfo}
          onRequestGPSPermission={onRequestGPSPermission}
          removePlace={removePlace}
        />
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}

const HeadingTextCell: Cell<HeadingTextCellProps> = ({ missionType }) => {
  const { t } = useTranslation('consoleMissionCreate')
  return (
    <CellFrame sx={{ py: 3, px: 5, mt: 2 }}>
      <Typography variant="h3">{t(`${missionType}MissionCreation`)}</Typography>
    </CellFrame>
  )
}
